import { gql, useMutation, useQuery } from '@apollo/client'
import omitBy from 'lodash/omitBy'
import { useEffect } from 'react'

import {
  BannerConnectionFragmentDoc,
  CurrentUserFragmentDoc,
  EntryCampaignRankingConnectionFragmentDoc,
  FeedConnectionFragmentDoc,
  FeedType,
  FollowArtistGachasConnectionFragmentDoc,
  FollowArtistMissionsConnectionFragmentDoc,
  GlobalEventFragmentDoc,
  GlobalHomeForGuestQuery,
  GlobalHomeForGuestQueryVariables,
  GlobalHomeOrganizationArtistRankingsConnectionFragmentDoc,
  GlobalHomeQuery,
  GlobalHomeQueryVariables,
  LikeCommentMutation,
  LikeCommentMutationVariables,
  LikePost,
  LikePostMutation,
  LikePostMutationVariables,
  MissionType,
  OrganizationNotificationConnectionFragmentDoc,
  RankingStatus,
  UnlikeCommentMutation,
  UnlikeCommentMutationVariables,
  UnlikePost,
  UnlikePostMutation,
  UnlikePostMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useAuth, useMessage, useRouter, useTrackPageView } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { setRedirectAfterLoginPath } from '@/core/native/storage'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user: currentUser, userClaims } = useCurrentUser()
  const { analytics } = useAnalytics()
  const { showError } = useMessage()
  const { reloadUser } = useAuth()
  const { data, loading, fetchMore, refetch } = useQuery<
    GlobalHomeQuery,
    GlobalHomeQueryVariables
  >(QUERY, {
    skip: !currentUser?.isSigned,
    variables: {
      pageInput: { limit: 20 },
      type: FeedType.Post,
      missionsTypeFilter: [MissionType.Quiz, MissionType.ItemCollect],
    },
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
  })
  const isNextFetch = !!data?.organization

  const { data: dataForGuest, loading: loadingForGuest } = useQuery<
    GlobalHomeForGuestQuery,
    GlobalHomeForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    skip: !!currentUser?.isSigned,
    returnPartialData: true,
    notifyOnNetworkStatusChange: true,
  })
  const isNextFetchForGuest = !!dataForGuest?.organization

  const [likePost] = useMutation<LikePostMutation, LikePostMutationVariables>(
    LikePost
  )
  const [unlikePost] = useMutation<
    UnlikePostMutation,
    UnlikePostMutationVariables
  >(UnlikePost)
  const [likeComment] = useMutation<
    LikeCommentMutation,
    LikeCommentMutationVariables
  >(LIKE_COMMENT)
  const [unlikeComment] = useMutation<
    UnlikeCommentMutation,
    UnlikeCommentMutationVariables
  >(UNLIKE_COMMENT)

  const feeds = data?.user?.feeds ?? null

  // GA userProperty
  // 無料会員 or 有料会員: 1つでも有料会員があればpaid userとする
  // エントリー中キャンペーン: 参加中のidをカンマ区切り
  const userType = currentUser?.isAnonymous
    ? 'guest'
    : data?.user?.organizationPatrons &&
      data.user.organizationPatrons.length > 0
    ? 'paid'
    : 'free'
  const entryCampaigns =
    data?.organization?.entryCampaignRankings?.map(ranking => {
      return ranking?.id
    }) || []
  analytics?.identify(
    currentUser,
    omitBy(
      {
        user_type: userType,
        entry_campaigns: entryCampaigns.join(',').slice(0, 36), // 36文字以下必須
      },
      v => !v
    )
  )

  const { location } = useRouter()

  useTrackPageView(() => {
    /**
     * HACK: delay for save
     */
    setTimeout(() => setRedirectAfterLoginPath(location.pathname), 300)
  })

  useEffect(() => {
    if (currentUser && !loading) {
      reloadUser().catch(showError)
    }
  }, [loading])

  return {
    state: {
      loading:
        (loading && !isNextFetch) || (loadingForGuest && !isNextFetchForGuest),
      user: data?.user ?? null,
      isLoggedIn: !!currentUser,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      banners:
        data?.organization?.banners ??
        dataForGuest?.organization?.banners ??
        null,
      feeds: feeds,
      prepaidPointBalance: data?.prepaidPointBalance ?? null,
      entryCampaignRankings: data?.organization?.entryCampaignRankings ?? null,
      followArtistGachas: data?.organization?.followArtistGachas ?? null,
      followArtistMissions: data?.organization?.followArtistMissions ?? null,
      blockedUsers: data?.user?.blockedUsers ?? [],
      organizationFollowees: data?.user?.organizationFollowees ?? null,
      organizationPatrons: data?.user?.organizationPatrons ?? null,
      topDisplayArtistIds:
        data?.organization?.topDisplayArtistIds ??
        dataForGuest?.organization?.topDisplayArtistIds ??
        null,
      notifications:
        data?.organizationOfficialNotifications?.edges.slice(0, 3) ??
        dataForGuest?.organizationOfficialNotifications?.edges.slice(0, 3) ??
        null,
      organizationArtistRankings:
        data?.organizationArtistRankings?.edges?.filter(
          ({ node }) => node?.status === RankingStatus.Active
        ) ?? null,
      events: data?.events ?? dataForGuest?.events ?? null,
    },

    actions: {
      onFilterFeeds: () =>
        refetch({
          type: FeedType.Comment,
        }),
      onFetchMoreFeeds: () =>
        fetchMore({
          variables: {
            cursor: feeds?.pageInfo?.endCursor,
            limit: 20,
          },
        }),
      async onLikePost(postId, isLike, likeCount) {
        if (isLike) {
          await unlikePost({
            variables: { unlikePostPostId: postId },
            optimisticResponse: ({ unlikePostPostId }) => ({
              unlikePost: {
                __typename: 'Post',
                id: unlikePostPostId,
                isLike: false,
                likeCount: --likeCount,
              },
            }),
          })
          return
        }
        await likePost({
          variables: { likePostPostId: postId },
          optimisticResponse: ({ likePostPostId }) => ({
            likePost: {
              __typename: 'Post',
              id: likePostPostId,
              isLike: true,
              likeCount: ++likeCount,
            },
          }),
        })
      },
      async onLikeComment(commentId, isLike, likeCount) {
        if (isLike) {
          await unlikeComment({
            variables: { unlikeCommentId: commentId },
            optimisticResponse: ({ unlikeCommentId }) => ({
              unlikeComment: {
                __typename: 'Comment',
                id: unlikeCommentId,
                isLike: false,
                likeCount: --likeCount,
              },
            }),
          })
          return
        }
        await likeComment({
          variables: { likeCommentId: commentId },
          optimisticResponse: ({ likeCommentId }) => ({
            likeComment: {
              __typename: 'Comment',
              id: likeCommentId,
              isLike: true,
              likeCount: ++likeCount,
            },
          }),
        })
      },
    },
  }
})

const QUERY = gql`
  query GlobalHome(
    $pageInput: PageInputDate
    $type: FeedType
    $followArtistGachasPageInput: PageInputDate
    $followArtistMissionsPageInput: PageInput
    $missionsTypeFilter: [MissionType!]
  ) {
    user {
      ...CurrentUser
      feeds(pageInput: $pageInput, type: $type) {
        ...FeedConnection
      }
    }
    organizationOfficialNotifications {
      ...OrganizationNotificationConnection
    }
    organization {
      id
      banners {
        ...BannerConnection
      }
      entryCampaignRankings {
        ...EntryCampaignRankingConnection
      }
      followArtistGachas(pageInput: $followArtistGachasPageInput) {
        ...FollowArtistGachasConnection
      }
      followArtistMissions(
        pageInput: $followArtistMissionsPageInput
        typeFilter: $missionsTypeFilter
      ) {
        ...FollowArtistMissionsConnection
      }
      topDisplayArtistIds
    }
    prepaidPointBalance {
      prepaidPointId
      balance
    }
    organizationArtistRankings(pageInput: { limit: 3 }) {
      ...GlobalHomeOrganizationArtistRankingsConnection
    }
    events(filter: all) {
      edges {
        node {
          ...GlobalEvent
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${CurrentUserFragmentDoc}
  ${GlobalEventFragmentDoc}
  ${FeedConnectionFragmentDoc}
  ${BannerConnectionFragmentDoc}
  ${EntryCampaignRankingConnectionFragmentDoc}
  ${FollowArtistGachasConnectionFragmentDoc}
  ${FollowArtistMissionsConnectionFragmentDoc}
  ${OrganizationNotificationConnectionFragmentDoc}
  ${GlobalHomeOrganizationArtistRankingsConnectionFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query GlobalHomeForGuest {
    organization {
      id
      banners {
        ...BannerConnection
      }
      topDisplayArtistIds
    }
    organizationOfficialNotifications {
      ...OrganizationNotificationConnection
    }
    events(filter: all) {
      edges {
        node {
          ...GlobalEvent
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
  ${BannerConnectionFragmentDoc}
  ${OrganizationNotificationConnectionFragmentDoc}
  ${GlobalEventFragmentDoc}
`

const LIKE_COMMENT = gql`
  mutation LikeComment($likeCommentId: ID!) {
    likeComment(id: $likeCommentId) {
      likeCount
      isLike
      id
    }
  }
`

const UNLIKE_COMMENT = gql`
  mutation UnlikeComment($unlikeCommentId: ID!) {
    unlikeComment(id: $unlikeCommentId) {
      likeCount
      isLike
      id
    }
  }
`
